import type { ChatStatus, MemberPresence } from "@beacon/core/chat"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useViewerZone } from "@/design-system/index.ts"
import { participantsQuery } from "./client.ts"
import { useChatLive } from "./live.tsx"
import { describeStatus, describeUntil, presenceQuery, statusShown } from "./presence.ts"
import {
  describeTyping,
  nextExpiry,
  noTyping,
  typistsAt,
  withTyping,
  type TypingState,
} from "./typing.ts"

/*
 * Presence, status and typing as Chat's screens show them: hooks reading how colleagues are seen,
 * and who is typing in a conversation, and the small marks that show a status.
 */

/**
 * How each of `ids` is seen now, read together and again every minute and on each hint about one
 * of them: a lookup by id, which answers nothing until the read arrives.
 */
export function usePresence(ids: readonly string[]): (id: string) => MemberPresence | undefined {
  const { data } = useQuery(presenceQuery(ids))
  return (id) => data?.get(id)
}

/** A clock that ticks every `every` ms while mounted, for what shows until a time passes. */
function useNow(every: number) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), every)
    return () => clearInterval(timer)
  }, [every])
  return now
}

/** A status as it shows now: none once its time to clear has passed, between reads. */
export function useStatusShown(status: ChatStatus | null | undefined) {
  const now = useNow(30_000)
  return statusShown(status ?? null, now)
}

/**
 * A colleague's status as a mark beside their name: its emoji (a speech bubble for words alone),
 * its words and when it clears in its tooltip and its label. Nothing when they have none.
 */
export function StatusMark({ status }: { status: ChatStatus | null | undefined }) {
  const shown = useStatusShown(status)
  const now = useNow(60_000)
  const zone = useViewerZone()
  if (!shown) return null
  const until = describeUntil(shown.until, now, zone)
  const said = [describeStatus(shown), until].filter(Boolean).join(". ")
  return (
    <span role="img" aria-label={`Status: ${said}`} title={said} className="shrink-0 text-ui">
      {shown.emoji ?? "💬"}
    </span>
  )
}

/**
 * Who is typing in the conversation now, as one line ("Ada is typing…"), empty when nobody is:
 * from the live socket's typing hints for it, each forgotten a few seconds after it was last said.
 * Names come from the conversation's participants, as only they may type in it.
 */
export function useTyping(conversationId: string): string {
  const { onHint } = useChatLive()
  const [state, setState] = useState<{ of: string; typing: TypingState }>({
    of: conversationId,
    typing: noTyping,
  })
  const [now, setNow] = useState(() => Date.now())
  const typing = state.of === conversationId ? state.typing : noTyping

  useEffect(
    () =>
      onHint((hint) => {
        if (hint.kind !== "typing" || hint.conversationId !== conversationId) return
        const at = Date.now()
        setState((was) => ({
          of: conversationId,
          typing: withTyping(was.of === conversationId ? was.typing : noTyping, hint, at),
        }))
        setNow(at)
      }),
    [onHint, conversationId],
  )

  // Wakes as the next typist's time runs out, so nobody shows typing after they stopped saying so.
  useEffect(() => {
    const next = nextExpiry(typing, now)
    if (next === undefined) return
    const timer = setTimeout(() => setNow(Date.now()), next - now + 50)
    return () => clearTimeout(timer)
  }, [typing, now])

  const typists = typistsAt(typing, now)
  const people = useQuery({ ...participantsQuery(conversationId), enabled: typists.length > 0 })
  const names = typists.flatMap((id) => {
    const name = people.data?.find((person) => person.id === id)?.name
    return name ? [name] : []
  })
  return describeTyping(names)
}
